import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { checkQuantity } from "../utils/quantityCheck";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import { ArrowDownward as ArrowDownIcon } from "@mui/icons-material";

/**
 * Stock Conversion - repack one item into another, e.g. 10 Kg of loose Dates into 10 packs of
 * "Dates 1 KG". The first item goes out of stock, the second comes in, and the packs cost what
 * the loose stock cost (total cost / packs made), so their sales show a real margin.
 *
 * Cost is worked out on the server from the source item's cost at the branch; the screen only
 * shows it. Nothing needs setting up in Production Def.
 */

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

/** Item picker that searches the server as the user types, like Wastage Entry. */
function ItemPicker({ label, value, onChange, tenancyId, auth }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const q = query.trim();
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`/api/${tenancyId}/items/search`, {
          ...auth,
          params: { q, page: 0, size: 25 },
        });
        setOptions(res.data?.content || []);
      } catch {
        setOptions([]);
        setError(t("Could not search items."));
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, tenancyId, auth, t]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={value}
      loading={loading}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v, why) => {
        if (why === "input") setQuery(v);
      }}
      filterOptions={(x) => x}
      noOptionsText={error || (query ? t("No items match") : t("Type to search items"))}
      getOptionLabel={(o) => (o ? `${o.itemName || ""}${o.itemCode ? ` (${o.itemCode})` : ""}` : "")}
      isOptionEqualToValue={(o, v) => o.itemId === v?.itemId}
      sx={{ flex: 1, minWidth: 240 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={t("Search item")}
          error={Boolean(error)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

const StockConversionPage = () => {
  const { t } = useTranslation();

  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState([]);
  const [voucherDate, setVoucherDate] = useState(today);
  const [source, setSource] = useState(null);
  const [sourceQty, setSourceQty] = useState("");
  const [target, setTarget] = useState(null);
  const [targetQty, setTargetQty] = useState("");
  const [batch, setBatch] = useState("");
  const [expiry, setExpiry] = useState("");
  const [remarks, setRemarks] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", severity: "info" });
  const [history, setHistory] = useState([]);
  const [fromDate, setFromDate] = useState(() => daysAgo(30));
  const [toDate, setToDate] = useState(today);

  const tenancyId = localStorage.getItem("tenancyId");
  const token = localStorage.getItem("jwtToken");
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const allowedBranches = useMemo(() => {
    try {
      const raw = localStorage.getItem("allowedBranches");
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/api/${tenancyId}/branches`, auth);
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.branches || res.data?.data || [];
        const filtered = allowedBranches.length
          ? list.filter((b) => allowedBranches.includes(b.branchCode))
          : list;
        setBranches(filtered);
        const loggedIn = localStorage.getItem("branchCode");
        if (filtered.length === 1) setBranch(filtered[0].branchCode);
        else if (loggedIn && filtered.some((b) => b.branchCode === loggedIn)) setBranch(loggedIn);
      } catch {
        if (!cancelled) {
          setMessage({ text: t("Could not load branches. Reload the page to try again."), severity: "error" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenancyId, auth, allowedBranches, t]);

  const payload = useCallback(
    () => ({
      branchCode: branch,
      voucherDate,
      sourceItemId: source?.itemId || null,
      sourceQty: Number(sourceQty) || 0,
      targetItemId: target?.itemId || null,
      targetQty: Number(targetQty) || 0,
      targetBatch: batch.trim() || null,
      targetExpiry: expiry || null,
      remarks: remarks.trim() || null,
    }),
    [branch, voucherDate, source, sourceQty, target, targetQty, batch, expiry, remarks]
  );

  // Stock on hand, cost and what would stop the save, refreshed as the form is filled.
  useEffect(() => {
    if (!branch || !source) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`/api/${tenancyId}/stock-conversion/preview`, payload(), auth);
        if (!cancelled) setPreview(res.data);
      } catch {
        if (!cancelled) setPreview(null);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [branch, source, target, sourceQty, targetQty, voucherDate, tenancyId, auth, payload]);

  const loadHistory = useCallback(async () => {
    if (!branch) {
      setHistory([]);
      return;
    }
    try {
      const res = await axios.get(`/api/${tenancyId}/stock-conversion`, {
        ...auth,
        params: { branchCode: branch, fromDate, toDate },
      });
      setHistory(Array.isArray(res.data) ? res.data : []);
    } catch {
      setHistory([]);
    }
  }, [branch, fromDate, toDate, tenancyId, auth]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const reset = () => {
    setSource(null);
    setSourceQty("");
    setTarget(null);
    setTargetQty("");
    setBatch("");
    setExpiry("");
    setRemarks("");
    setPreview(null);
  };

  const save = async () => {
    setMessage({ text: "", severity: "info" });
    if (!branch) return setMessage({ text: t("Select a branch"), severity: "warning" });
    if (!source) return setMessage({ text: t("Pick the item to convert"), severity: "warning" });
    if (!target) return setMessage({ text: t("Pick the item it becomes"), severity: "warning" });
    for (const v of [sourceQty, targetQty]) {
      const q = checkQuantity(v);
      if (!q.ok || !(Number(v) > 0)) {
        return setMessage({ text: q.error || t("Enter both quantities"), severity: "warning" });
      }
    }
    setSaving(true);
    try {
      const res = await axios.post(`/api/${tenancyId}/stock-conversion`, payload(), auth);
      const d = res.data;
      setMessage({
        text: `${t("Converted")} — ${d.voucherNumber}: ${fmtQty(d.sourceQty)} ${d.sourceUnit || ""} ${d.sourceItemName} → ${fmtQty(d.targetQty)} ${d.targetUnit || ""} ${d.targetItemName}`,
        severity: "success",
      });
      reset();
      loadHistory();
    } catch (err) {
      setMessage({ text: err?.response?.data?.message || t("Could not save. Try again."), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const money = (n) =>
    n == null ? "—" : (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtQty = (n) => (n == null ? "" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

  const srcUnit = source?.unitName || preview?.sourceUnit || "";
  const tgtUnit = target?.unitName || preview?.targetUnit || "";
  const problems = (preview?.problems || []).filter(
    // Missing fields are obvious on the form; only show what the user would not see otherwise.
    (p) => !/^(Pick the|Enter the)/.test(p)
  );
  const headCell = { color: "#fff", fontWeight: 700 };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: "100vw", boxSizing: "border-box" }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        {t("Stock Conversion")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("Convert stock of one item into another, for example 10 Kg of loose Dates into 10 packs of Dates 1 KG. The cost moves with it.")}
      </Typography>

      {message.text && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage({ text: "", severity: "info" })}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>{t("Branch")}</InputLabel>
            <Select value={branch} label={t("Branch")} onChange={(e) => setBranch(e.target.value)}>
              {branches.map((b) => (
                <MenuItem key={b.branchCode} value={b.branchCode}>
                  {b.branchCode}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label={t("Date")}
            value={voucherDate}
            onChange={(e) => setVoucherDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today() }}
            sx={{ minWidth: 170 }}
          />
        </Stack>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("Convert")}
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
          <ItemPicker label={t("Item to convert")} value={source} onChange={setSource} tenancyId={tenancyId} auth={auth} />
          <TextField
            size="small"
            label={`${t("Quantity")}${srcUnit ? ` (${srcUnit})` : ""}`}
            value={sourceQty}
            onChange={(e) => setSourceQty(e.target.value)}
            inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
            helperText={
              preview && source
                ? `${t("In stock")}: ${fmtQty(preview.sourceStock)} ${srcUnit} · ${t("Cost")}: ${money(preview.sourceUnitCost)}`
                : " "
            }
            sx={{ width: { md: 260 } }}
          />
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "center", my: 0.5 }}>
          <ArrowDownIcon color="action" />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("Into")}
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
          <ItemPicker label={t("Item it becomes")} value={target} onChange={setTarget} tenancyId={tenancyId} auth={auth} />
          <TextField
            size="small"
            label={`${t("Quantity made")}${tgtUnit ? ` (${tgtUnit})` : ""}`}
            value={targetQty}
            onChange={(e) => setTargetQty(e.target.value)}
            inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
            helperText={
              preview?.targetUnitCost != null && Number(targetQty) > 0
                ? `${t("Cost each")}: ${money(preview.targetUnitCost)}`
                : " "
            }
            sx={{ width: { md: 260 } }}
          />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }}>
          <TextField size="small" label={t("Batch (optional)")} value={batch} onChange={(e) => setBatch(e.target.value)} sx={{ minWidth: 160 }} />
          <TextField
            size="small"
            type="date"
            label={t("Expiry (optional)")}
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 170 }}
          />
          <TextField size="small" fullWidth label={t("Remarks")} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </Stack>

        {preview && source && target && Number(sourceQty) > 0 && Number(targetQty) > 0 && (
          <Alert severity={problems.length ? "warning" : "info"} sx={{ mt: 2 }}>
            {problems.length ? (
              problems.join(". ")
            ) : preview.targetUnitCost == null ? (
              t("No cost is known for the item to convert, so the new stock will have no cost. Enter its purchase first if you want margins on it.")
            ) : (
              `${fmtQty(preview.sourceQty)} ${srcUnit} ${preview.sourceItemName} × ${money(preview.sourceUnitCost)} = ${money(preview.totalCost)} → ${fmtQty(preview.targetQty)} ${tgtUnit} ${preview.targetItemName} ${t("at")} ${money(preview.targetUnitCost)} ${t("each")}`
            )}
          </Alert>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={save} disabled={saving || !branch || problems.length > 0}>
            {saving ? <CircularProgress size={22} /> : t("Convert")}
          </Button>
          <Button onClick={reset} disabled={saving}>
            {t("Clear")}
          </Button>
        </Stack>
      </Paper>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t("Recent conversions")}
        </Typography>
        <TextField size="small" type="date" label={t("From")} value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label={t("To")} value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
      </Stack>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#1976d2" }}>
              <TableCell sx={headCell}>{t("Voucher")}</TableCell>
              <TableCell sx={headCell}>{t("Date")}</TableCell>
              <TableCell sx={headCell}>{t("Converted")}</TableCell>
              <TableCell sx={headCell}>{t("Into")}</TableCell>
              <TableCell sx={headCell} align="right">{t("Cost each")}</TableCell>
              <TableCell sx={headCell} align="right">{t("Total cost")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary" }}>
                  {branch ? t("No conversions in this period") : t("Select a branch")}
                </TableCell>
              </TableRow>
            ) : (
              history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>{h.voucherNumber}</TableCell>
                  <TableCell>{h.voucherDate ? String(h.voucherDate).slice(0, 10) : ""}</TableCell>
                  <TableCell>{`${fmtQty(h.sourceQty)} ${h.sourceUnit || ""} ${h.sourceItemName || ""}`}</TableCell>
                  <TableCell>{`${fmtQty(h.targetQty)} ${h.targetUnit || ""} ${h.targetItemName || ""}`}</TableCell>
                  <TableCell align="right">{money(h.targetUnitCost)}</TableCell>
                  <TableCell align="right">{money(h.totalCost)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StockConversionPage;
